import { useMemo, useState } from 'react';
import { Alert, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { enrichUsageWithCompany } from '../../../helpers/creditUsageDisplay';
import {
  dismissLowBalanceBanner,
  isLowBalanceBannerDismissed,
  lowBalanceDismissalKey,
  shouldShowLowBalanceBanner,
} from '../../../helpers/lowBalanceUpgradePrompt';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';
import StyledButton from '../Button/StyledButton';

/**
 * Warns when the company's remaining allowance drops below the equivalent of
 * 100 call minutes (specs/low-balance-upgrade-banner.md). Self-gating: renders
 * null unless the low-balance trigger fires and the current billing period has
 * not been dismissed.
 */
const LowBalanceBanner: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { usage, company, subscription } = useSubscription();
  const { track } = useAnalytics();
  const [dismissedNow, setDismissedNow] = useState(false);

  const displayUsage = useMemo(
    () => enrichUsageWithCompany(usage, company),
    [usage, company]
  );

  const companyId = (currentUser?.company_id as string) || '';
  if (!currentUser || !companyId) return null;
  if (!shouldShowLowBalanceBanner(displayUsage, company ?? null)) return null;

  const dismissalKey = lowBalanceDismissalKey(companyId, subscription);
  if (dismissedNow || isLowBalanceBannerDismissed(dismissalKey)) return null;

  const canManageBilling = canPerformAction(
    currentUser,
    PERMISSIONS.MANAGE_BILLING
  );

  const handleDismiss = () => {
    dismissLowBalanceBanner(dismissalKey);
    setDismissedNow(true);
  };

  const handleUpgradeClick = () => {
    track(AnalyticsEvents.UPGRADE_BUTTON_CLICKED, {
      source: 'low_balance_banner',
    });
  };

  return (
    <Box sx={{ px: { xs: 1, md: 3 }, pt: 2 }}>
      <Alert
        severity="warning"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canManageBilling ? (
              <Link
                to="/pricing"
                style={{ textDecoration: 'none' }}
                onClick={handleUpgradeClick}
              >
                <StyledButton size="small" variant="contained" color="success">
                  {t('Upgrade')}
                </StyledButton>
              </Link>
            ) : null}
            <IconButton
              aria-label={t('Close icon')}
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        {t(
          'Your balance is running low — less than the equivalent of 100 call minutes remains this period.'
        )}
        {!canManageBilling
          ? ` ${t('Contact your account admin to upgrade your plan.')}`
          : ''}
      </Alert>
    </Box>
  );
};

export default LowBalanceBanner;
